import * as v from 'valibot';

/**
 * The vocabulary of an automation: what one is bound to, how it is addressed,
 * and when repeated failure takes it out of service.
 *
 * An automation is deliberately **not a new binding**. Reef already stores
 * every way a run can start without a person — a `reef_cron_trigger` row per
 * schedule, a `reef_trigger_subscription` row per event narrowing — and
 * `syncWorkflow` is their only writer, which is what makes them unable to
 * disagree with the committed document. A table that named those bindings a
 * second time would be a second thing to keep in step with the document, and
 * the mirror is the half nobody reviews. So an automation is an *identity over
 * a binding that already exists*, and the row reef adds alongside it carries
 * only state: when it last fired, what happened, how many times in a row it has
 * failed, and whether that has paused it.
 */

/**
 * The two shapes of unattended start.
 *
 * `schedule` is the clock: one automation per distinct cron expression on a
 * workflow, which is exactly how `reef_cron_trigger` is keyed. `event` is a
 * delivery: one automation per `(source, event)` a workflow subscribes to,
 * which collapses the several subscription rows one trigger produces — a
 * trigger narrowed by two labels is two rows and one automation, for the same
 * reason two matching rows are one run.
 */
export const AutomationKind = v.picklist(['schedule', 'event']);
export type AutomationKind = v.InferOutput<typeof AutomationKind>;

/** What an automation is bound to. Together these three are its identity. */
export interface AutomationBinding {
  kind: AutomationKind;
  workflowId: string;
  /**
   * The cron expression for a `schedule`, `<source>.<event>` for an `event`.
   *
   * The same string the underlying row is keyed on, so an automation's state
   * survives a re-commit that leaves the binding unchanged — and a *changed*
   * schedule is a different automation with a clean history, which is the
   * honest answer: its failures were another expression's.
   */
  key: string;
}

/**
 * Why a firing happened, which is the field that keeps the history readable.
 *
 * `schedule` and `delivery` are unattended — nobody was there, and these are
 * the two that move the failure counter. `manual` and `replay` are somebody
 * pressing a button, and they are recorded in the same history so that "this
 * last succeeded at 09:14" is never quietly a person testing it by hand.
 */
export const AutomationFireMode = v.picklist(['schedule', 'delivery', 'manual', 'replay']);
export type AutomationFireMode = v.InferOutput<typeof AutomationFireMode>;

/** How one firing ended. `started` means a run exists; it may still fail later. */
export const AutomationFireStatus = v.picklist(['started', 'failed']);
export type AutomationFireStatus = v.InferOutput<typeof AutomationFireStatus>;

/**
 * Consecutive unattended failures before an automation pauses itself.
 *
 * Three, and the number is a compromise between two schedules with very
 * different clocks. Two is too few: a deployment rolling out takes a minute or
 * so, and an every-minute cron can lose two ticks to a deploy that nothing was
 * wrong with — pausing there would train people to treat the pause as noise,
 * which is the one outcome that makes the feature worthless. Five is too many
 * in the other direction: a daily schedule would go five days silent, and the
 * failure this exists to catch is the 2am one nobody is watching.
 *
 * At three, a minutely schedule tolerates a two-minute blip and a daily one is
 * paused inside the week — and, more to the point, three failures in a row is
 * where "transient" stops being the likeliest explanation.
 */
export const AUTOMATION_FAILURE_LIMIT = 3;

/**
 * Address one automation as a single URL-safe token.
 *
 * Reversible, and that is the point rather than a convenience: an automation
 * that has never fired has no state row, and it still has to be addressable —
 * otherwise the first thing you could do with a newly committed schedule would
 * be nothing at all. Decoding is a string operation, so `GET /automations/:id`
 * resolves its binding before it touches the database, and a token naming a
 * workflow the caller cannot see is refused by the same visibility check every
 * other route uses rather than by the id being unguessable. It is an address,
 * not a credential.
 */
export function automationId(binding: AutomationBinding): string {
  return `aut_${base64url(`${binding.kind}\n${binding.workflowId}\n${binding.key}`)}`;
}

/** The inverse, or null for anything that is not one of reef's own tokens. */
export function parseAutomationId(id: string): AutomationBinding | null {
  if (!id.startsWith('aut_')) return null;

  let decoded: string;
  try {
    decoded = fromBase64url(id.slice(4));
  } catch {
    return null;
  }

  // Split on the first two separators only: a cron expression cannot contain a
  // newline, but nothing here should depend on that being true forever.
  const first = decoded.indexOf('\n');
  const second = decoded.indexOf('\n', first + 1);
  if (first === -1 || second === -1) return null;

  const kind = v.safeParse(AutomationKind, decoded.slice(0, first));
  const workflowId = decoded.slice(first + 1, second);
  const key = decoded.slice(second + 1);
  if (!kind.success || !workflowId || !key) return null;

  return { kind: kind.output, workflowId, key };
}

/** `<source>.<event>`, the key an event automation is addressed by. */
export function eventAutomationKey(source: string, event: string): string {
  return `${source}.${event}`;
}

/**
 * The inverse. Split on the *first* dot, because every source name is one word
 * and several providers' event names are not — splitting on the last would look
 * right for `github.push` and quietly break `linear.issue.created`.
 */
export function splitEventAutomationKey(key: string): { source: string; event: string } {
  const index = key.indexOf('.');
  if (index === -1) return { source: key, event: '*' };
  return { source: key.slice(0, index), event: key.slice(index + 1) };
}

function base64url(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64url(value: string): string {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
